import React, { useState, useEffect, useRef, useCallback } from 'react';

const API = 'https://sashtimedix.in/api/driver';

// ---------- Theme ----------
const C = {
  bg: '#0b0f14',
  bg2: '#12181f',
  card: '#161d26',
  cardHover: '#1c2530',
  border: '#232d38',
  primary: '#22c55e',
  primaryDark: '#16a34a',
  danger: '#ef4444',
  warn: '#f59e0b',
  text: '#e8edf2',
  textDim: '#8b98a5',
  textFaint: '#5b6673',
  accent: '#3b82f6',
};

const statusMap = {
  confirmed: { label: 'Confirmed', color: C.accent },
  ready: { label: 'Ready for pickup', color: C.warn },
  packed: { label: 'Packed', color: C.warn },
  out_for_delivery: { label: 'Out for delivery', color: C.primary },
  delivered: { label: 'Delivered', color: C.textDim },
};

function fmtMoney(n) {
  return '₹' + (Number(n) || 0).toLocaleString('en-IN');
}

function timeAgo(dateStr) {
  const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 60000);
  if (diff < 1) return 'just now';
  if (diff < 60) return `${diff}m ago`;
  const h = Math.floor(diff / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

// ---------- Icons (inline SVG, no external deps) ----------
const Icon = {
  home: (p) => <svg viewBox="0 0 24 24" width={p?.s||20} height={p?.s||20} fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 12l9-9 9 9M5 10v10a1 1 0 001 1h4v-6h4v6h4a1 1 0 001-1V10"/></svg>,
  orders: (p) => <svg viewBox="0 0 24 24" width={p?.s||20} height={p?.s||20} fill="none" stroke="currentColor" strokeWidth="2"><rect x="4" y="4" width="16" height="16" rx="2"/><path d="M8 9h8M8 13h8M8 17h5"/></svg>,
  wallet: (p) => <svg viewBox="0 0 24 24" width={p?.s||20} height={p?.s||20} fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="6" width="18" height="13" rx="2"/><path d="M3 10h18M16 15h2"/></svg>,
  profile: (p) => <svg viewBox="0 0 24 24" width={p?.s||20} height={p?.s||20} fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="8" r="4"/><path d="M4 21c1.5-4 5-6 8-6s6.5 2 8 6"/></svg>,
  pin: (p) => <svg viewBox="0 0 24 24" width={p?.s||16} height={p?.s||16} fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 21s7-6.5 7-12a7 7 0 10-14 0c0 5.5 7 12 7 12z"/><circle cx="12" cy="9" r="2.5"/></svg>,
  bike: (p) => <svg viewBox="0 0 24 24" width={p?.s||18} height={p?.s||18} fill="none" stroke="currentColor" strokeWidth="2"><circle cx="5.5" cy="17.5" r="3.5"/><circle cx="18.5" cy="17.5" r="3.5"/><path d="M5.5 17.5L9 10h5l3 3h1.5M9 10L11 6h3"/></svg>,
  check: (p) => <svg viewBox="0 0 24 24" width={p?.s||18} height={p?.s||18} fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M20 6L9 17l-5-5"/></svg>,
  x: (p) => <svg viewBox="0 0 24 24" width={p?.s||18} height={p?.s||18} fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 6L6 18M6 6l12 12"/></svg>,
  power: (p) => <svg viewBox="0 0 24 24" width={p?.s||18} height={p?.s||18} fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2v9M18.4 6.6a9 9 0 11-12.8 0"/></svg>,
  phone: (p) => <svg viewBox="0 0 24 24" width={p?.s||16} height={p?.s||16} fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.9v3a2 2 0 01-2.2 2 19.8 19.8 0 01-8.6-3 19.5 19.5 0 01-6-6 19.8 19.8 0 01-3-8.7A2 2 0 014.1 2h3a2 2 0 012 1.7c.1 1 .3 2 .6 2.9a2 2 0 01-.5 2.1L8 10a16 16 0 006 6l1.3-1.2a2 2 0 012.1-.5c.9.3 1.9.5 2.9.6a2 2 0 011.7 2z"/></svg>,
  arrow: (p) => <svg viewBox="0 0 24 24" width={p?.s||18} height={p?.s||18} fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18l6-6-6-6"/></svg>,
  navigate: (p) => <svg viewBox="0 0 24 24" width={p?.s||18} height={p?.s||18} fill="none" stroke="currentColor" strokeWidth="2"><polygon points="3 11 22 2 13 21 11 13 3 11"/></svg>,
  star: (p) => <svg viewBox="0 0 24 24" width={p?.s||14} height={p?.s||14} fill="currentColor"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 21 12 17.77 5.82 21 7 14.14 2 9.27l6.91-1.01z"/></svg>,
};

// ---------- Reusable pieces ----------
function Badge({ children, color }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      background: color + '20', color, fontSize: 12, fontWeight: 600,
      padding: '4px 10px', borderRadius: 20,
    }}>
      <span style={{ width: 6, height: 6, borderRadius: 99, background: color }} />
      {children}
    </span>
  );
}

function Button({ children, onClick, variant = 'primary', full, disabled, style = {} }) {
  const variants = {
    primary: { background: C.primary, color: '#08150c' },
    danger: { background: C.danger, color: '#fff' },
    ghost: { background: 'transparent', color: C.text, border: `1px solid ${C.border}` },
    dark: { background: C.card, color: C.text, border: `1px solid ${C.border}` },
  };
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        ...variants[variant],
        width: full ? '100%' : 'auto',
        padding: '13px 20px',
        borderRadius: 12,
        border: variants[variant].border || 'none',
        fontSize: 15,
        fontWeight: 700,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        transition: 'transform .1s, opacity .15s',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        ...style,
      }}
      onMouseDown={(e) => { if (!disabled) e.currentTarget.style.transform = 'scale(0.97)'; }}
      onMouseUp={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
    >
      {children}
    </button>
  );
}

function EmptyState({ icon, title, sub }) {
  return (
    <div style={{ textAlign: 'center', padding: '60px 20px', color: C.textDim }}>
      <div style={{ fontSize: 40, marginBottom: 12, opacity: 0.5 }}>{icon}</div>
      <div style={{ fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 4 }}>{title}</div>
      <div style={{ fontSize: 13 }}>{sub}</div>
    </div>
  );
}

// ---------- Login Screen ----------
function LoginScreen({ onLogin }) {
  const [mode, setMode] = useState('login');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [vehicleType, setVehicleType] = useState('bike');
  const [vehicleNumber, setVehicleNumber] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    setError('');
    if (!phone || !password || (mode === 'register' && !name)) {
      setError('Please fill all required fields');
      return;
    }
    setLoading(true);
    try {
      if (mode === 'register') {
        const res = await fetch(`${API}/register`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name, phone, password, vehicleType, vehicleNumber }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Registration failed');
        setMode('login');
        setError('');
      } else {
        const res = await fetch(`${API}/login`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ phone, password }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Login failed');
        localStorage.setItem('driverToken', data.token);
        localStorage.setItem('driverInfo', JSON.stringify(data.driver));
        onLogin(data.token, data.driver);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: `radial-gradient(circle at 50% 0%, #1a2530 0%, ${C.bg} 60%)`, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: 24 }}>
      <div style={{ maxWidth: 380, margin: '0 auto', width: '100%' }}>
        <div style={{ textAlign: 'center', marginBottom: 36 }}>
          <div style={{
            width: 64, height: 64, borderRadius: 20, background: `linear-gradient(135deg, ${C.primary}, ${C.primaryDark})`,
            display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px',
            boxShadow: `0 8px 24px ${C.primary}40`,
          }}>
            <Icon.bike s={32} />
          </div>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: C.text, margin: 0 }}>SashtiMedix</h1>
          <p style={{ color: C.textDim, fontSize: 14, marginTop: 4 }}>Delivery Partner App</p>
        </div>

        <div style={{ display: 'flex', background: C.card, borderRadius: 12, padding: 4, marginBottom: 20, border: `1px solid ${C.border}` }}>
          {['login', 'register'].map((m) => (
            <button key={m} onClick={() => { setMode(m); setError(''); }} style={{
              flex: 1, padding: '10px', borderRadius: 9, border: 'none', cursor: 'pointer',
              background: mode === m ? C.primary : 'transparent',
              color: mode === m ? '#08150c' : C.textDim,
              fontWeight: 700, fontSize: 14, transition: 'all .15s',
            }}>{m === 'login' ? 'Login' : 'Register'}</button>
          ))}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {mode === 'register' && (
            <input placeholder="Full Name" value={name} onChange={(e) => setName(e.target.value)} style={inputStyle} />
          )}
          <input placeholder="Phone Number" value={phone} onChange={(e) => setPhone(e.target.value)} style={inputStyle} maxLength={10} />
          <input placeholder="Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} style={inputStyle} />
          {mode === 'register' && (
            <>
              <select value={vehicleType} onChange={(e) => setVehicleType(e.target.value)} style={inputStyle}>
                <option value="bike">Bike</option>
                <option value="scooter">Scooter</option>
                <option value="cycle">Cycle</option>
              </select>
              <input placeholder="Vehicle Number (e.g. TN01AB1234)" value={vehicleNumber} onChange={(e) => setVehicleNumber(e.target.value)} style={inputStyle} />
            </>
          )}

          {error && (
            <div style={{ background: C.danger + '15', color: C.danger, padding: '10px 14px', borderRadius: 10, fontSize: 13, fontWeight: 600 }}>
              {error}
            </div>
          )}

          <Button onClick={submit} disabled={loading} full style={{ marginTop: 8 }}>
            {loading ? 'Please wait...' : mode === 'login' ? 'Login' : 'Create Account'}
          </Button>
        </div>
      </div>
    </div>
  );
}

const inputStyle = {
  background: C.card,
  border: `1px solid ${C.border}`,
  borderRadius: 12,
  padding: '14px 16px',
  color: C.text,
  fontSize: 15,
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
};

// ---------- Order Card ----------
function IconButton({ icon, onClick, color }) {
  return (
    <button onClick={onClick} style={{
      width: 38, height: 38, borderRadius: 12, border: `1px solid ${C.border}`,
      background: C.bg2, color: color || C.text, display: 'flex', alignItems: 'center',
      justifyContent: 'center', cursor: 'pointer',
    }}>
      {icon}
    </button>
  );
}

function OrderCard({ order, actionLabel, actionColor, onAction, secondaryAction }) {
  const st = statusMap[order.status] || { label: order.status, color: C.textDim };
  const isActive = order.status !== 'delivered';
  const customerPhone = order.customerPhone || order.phone || order.userPhone;
  const dropAddress = order.address || order.deliveryAddress || '';

  const callCustomer = () => { if (customerPhone) window.location.href = `tel:${customerPhone}`; };
  const openNavigation = () => {
    const dest = encodeURIComponent(dropAddress || 'Chennai');
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${dest}`, '_blank');
  };

  return (
    <div style={{
      background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: 16, marginBottom: 12,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
        <div>
          <div style={{ fontWeight: 800, fontSize: 16, color: C.text }}>#{order.orderId}</div>
          <div style={{ fontSize: 12, color: C.textFaint, marginTop: 2 }}>{timeAgo(order.createdAt)}</div>
        </div>
        <Badge color={st.color}>{st.label}</Badge>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          <div style={{ color: C.warn, marginTop: 2 }}><Icon.pin /></div>
          <div style={{ fontSize: 13, color: C.textDim, lineHeight: 1.4 }}>
            <span style={{ color: C.textFaint, fontSize: 11 }}>PICKUP</span><br />
            {order.vendorName || 'Pharmacy'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          <div style={{ color: C.primary, marginTop: 2 }}><Icon.pin /></div>
          <div style={{ fontSize: 13, color: C.textDim, lineHeight: 1.4 }}>
            <span style={{ color: C.textFaint, fontSize: 11 }}>DROP</span><br />
            {dropAddress || 'Customer address'}
          </div>
        </div>
      </div>

      {isActive && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          <IconButton icon={<Icon.phone s={16} />} onClick={callCustomer} color={customerPhone ? C.primary : C.textFaint} />
          <IconButton icon={<Icon.navigate s={16} />} onClick={openNavigation} color={C.accent} />
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 12, borderTop: `1px solid ${C.border}` }}>
        <div style={{ fontWeight: 800, fontSize: 18, color: C.primary }}>{fmtMoney(order.total)}</div>
        <div style={{ display: 'flex', gap: 8 }}>
          {secondaryAction}
          {actionLabel && (
            <Button variant={actionColor === C.danger ? 'danger' : 'primary'} onClick={() => onAction(order)} style={{ padding: '10px 18px', fontSize: 13 }}>
              {actionLabel}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------- Sound + Vibration alert for new orders ----------
function playAlertSound() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    [0, 0.18, 0.36].forEach((delay) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = 880;
      gain.gain.setValueAtTime(0.25, ctx.currentTime + delay);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + delay + 0.15);
      osc.connect(gain); gain.connect(ctx.destination);
      osc.start(ctx.currentTime + delay);
      osc.stop(ctx.currentTime + delay + 0.16);
    });
  } catch (e) { /* audio not supported */ }
  if (navigator.vibrate) navigator.vibrate([200, 100, 200, 100, 200]);
}

// ---------- Live GPS tracking hook ----------
function useLiveLocation(token, isOnline, onUpdate) {
  useEffect(() => {
    if (!isOnline || !navigator.geolocation) return;
    const sendLocation = (pos) => {
      const { latitude, longitude } = pos.coords;
      if (onUpdate) onUpdate(latitude, longitude);
      fetch(`${API}/location`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ lat: latitude, lng: longitude }),
      }).catch(() => {});
    };
    const watchId = navigator.geolocation.watchPosition(sendLocation, () => {}, {
      enableHighAccuracy: true, maximumAge: 10000, timeout: 15000,
    });
    const iv = setInterval(() => {
      navigator.geolocation.getCurrentPosition(sendLocation, () => {});
    }, 15000);
    return () => { navigator.geolocation.clearWatch(watchId); clearInterval(iv); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, isOnline]);
}

// ---------- Live Map (uses Leaflet loaded via CDN in index.html) ----------
function LiveMap({ lat, lng }) {
  const mapRef = useRef(null);
  const leafletMap = useRef(null);
  const marker = useRef(null);

  useEffect(() => {
    if (!window.L || !mapRef.current) return;
    if (!leafletMap.current) {
      leafletMap.current = window.L.map(mapRef.current, { zoomControl: false, attributionControl: false }).setView([lat || 13.0827, lng || 80.2707], 15);
      window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 19 }).addTo(leafletMap.current);
      const icon = window.L.divIcon({
        html: `<div style="width:18px;height:18px;border-radius:50%;background:${C.primary};border:3px solid #fff;box-shadow:0 0 0 4px ${C.primary}40;"></div>`,
        className: '', iconSize: [18, 18], iconAnchor: [9, 9],
      });
      marker.current = window.L.marker([lat || 13.0827, lng || 80.2707], { icon }).addTo(leafletMap.current);
    }
    return () => {
      if (leafletMap.current) { leafletMap.current.remove(); leafletMap.current = null; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (leafletMap.current && marker.current && lat && lng) {
      marker.current.setLatLng([lat, lng]);
      leafletMap.current.setView([lat, lng]);
    }
  }, [lat, lng]);

  return <div ref={mapRef} style={{ width: '100%', height: 180, borderRadius: 16, overflow: 'hidden', border: `1px solid ${C.border}` }} />;
}

// ---------- Home / Available Orders Screen ----------
function HomeScreen({ token, driver, onDriverUpdate }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState('');
  const isOnline = driver?.isOnline;
  const prevOrderIds = useRef(new Set());
  const firstLoad = useRef(true);
  const [myLocation, setMyLocation] = useState(null);

  useLiveLocation(token, isOnline, (lat, lng) => setMyLocation({ lat, lng }));

  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  const fetchOrders = useCallback(async () => {
    try {
      const res = await fetch(`${API}/orders/available`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      const list = Array.isArray(data) ? data : [];

      const currentIds = new Set(list.map((o) => o._id));
      if (!firstLoad.current) {
        const isNew = list.some((o) => !prevOrderIds.current.has(o._id));
        if (isNew) {
          playAlertSound();
          if ('Notification' in window && Notification.permission === 'granted') {
            new Notification('New order available!', { body: 'Tap to view and accept in the app.' });
          }
        }
      }
      prevOrderIds.current = currentIds;
      firstLoad.current = false;

      setOrders(list);
    } catch (e) { /* silent */ }
    setLoading(false);
  }, [token]);

  useEffect(() => {
    fetchOrders();
    const iv = setInterval(fetchOrders, 8000);
    return () => clearInterval(iv);
  }, [fetchOrders]);

  const toggleOnline = async () => {
    try {
      const res = await fetch(`${API}/status`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ isOnline: !isOnline }),
      });
      const data = await res.json();
      onDriverUpdate(data);
    } catch (e) {}
  };

  const acceptOrder = async (order) => {
    try {
      const res = await fetch(`${API}/orders/${order._id}/accept`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) { setToast(data.error || 'Could not accept order'); setTimeout(() => setToast(''), 2500); return; }
      setToast('Order accepted! Check "My Deliveries"');
      setTimeout(() => setToast(''), 2500);
      fetchOrders();
    } catch (e) {}
  };

  const [rejectOrderTarget, setRejectOrderTarget] = useState(null);

  const rejectOrder = async (order, reason) => {
    try {
      await fetch(`${API}/orders/${order._id}/reject`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ reason }),
      });
      setRejectOrderTarget(null);
      fetchOrders();
    } catch (e) {}
  };

  return (
    <div style={{ padding: '20px 16px 100px' }}>
      <div style={{
        background: `linear-gradient(135deg, ${C.card}, ${C.bg2})`, border: `1px solid ${C.border}`,
        borderRadius: 18, padding: 18, marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <div>
          <div style={{ fontSize: 13, color: C.textDim }}>Welcome back,</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: C.text }}>{driver?.name}</div>
        </div>
        <button onClick={toggleOnline} style={{
          display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', borderRadius: 30,
          border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 13,
          background: isOnline ? C.primary + '20' : C.danger + '20',
          color: isOnline ? C.primary : C.danger,
        }}>
          <Icon.power s={16} />
          {isOnline ? 'Online' : 'Offline'}
        </button>
      </div>

      {toast && (
        <div style={{ background: C.primary + '20', color: C.primary, padding: '12px 16px', borderRadius: 12, marginBottom: 16, fontSize: 13, fontWeight: 600, textAlign: 'center' }}>
          {toast}
        </div>
      )}

      {!isOnline && (
        <div style={{ background: C.warn + '15', color: C.warn, padding: '14px 16px', borderRadius: 14, marginBottom: 16, fontSize: 13, fontWeight: 600, textAlign: 'center' }}>
          You're offline. Go online to start receiving orders.
        </div>
      )}

      {isOnline && myLocation && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.textDim, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Your Live Location
          </div>
          <LiveMap lat={myLocation.lat} lng={myLocation.lng} />
        </div>
      )}

      <div style={{ fontSize: 14, fontWeight: 700, color: C.textDim, marginBottom: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        Available Orders {orders.length > 0 && `(${orders.length})`}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', color: C.textDim, padding: 40 }}>Loading...</div>
      ) : orders.length === 0 ? (
        <EmptyState icon="📦" title="No orders right now" sub="New orders will appear here automatically" />
      ) : (
        orders.map((o) => (
          <OrderCard
            key={o._id}
            order={o}
            actionLabel="Accept"
            onAction={acceptOrder}
            secondaryAction={
              <Button variant="ghost" onClick={() => setRejectOrderTarget(o)} style={{ padding: '10px 14px', fontSize: 13 }}>
                Reject
              </Button>
            }
          />
        ))
      )}

      {rejectOrderTarget && (
        <RejectModal
          order={rejectOrderTarget}
          onClose={() => setRejectOrderTarget(null)}
          onSubmit={(reason) => rejectOrder(rejectOrderTarget, reason)}
        />
      )}
    </div>
  );
}

// ---------- My Deliveries Screen ----------
function RejectModal({ order, onClose, onSubmit }) {
  const [reason, setReason] = useState('');
  const reasons = ['Too far away', 'Low delivery fee', 'Traffic/weather issue', 'Vehicle issue', 'Other'];
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 20,
    }}>
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 20, padding: 24, width: '100%', maxWidth: 340 }}>
        <div style={{ fontSize: 17, fontWeight: 800, color: C.text, marginBottom: 6 }}>Reject Order #{order.orderId}</div>
        <div style={{ fontSize: 13, color: C.textDim, marginBottom: 16 }}>Let us know why (optional) - helps us improve.</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
          {reasons.map((r) => (
            <button key={r} onClick={() => setReason(r)} style={{
              textAlign: 'left', padding: '12px 14px', borderRadius: 12, cursor: 'pointer',
              border: `1px solid ${reason === r ? C.primary : C.border}`,
              background: reason === r ? C.primary + '15' : C.bg2,
              color: reason === r ? C.primary : C.text, fontSize: 13, fontWeight: 600,
            }}>
              {r}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <Button variant="ghost" onClick={onClose} style={{ flex: 1 }}>Cancel</Button>
          <Button variant="danger" onClick={() => onSubmit(reason)} style={{ flex: 1 }}>Reject</Button>
        </div>
      </div>
    </div>
  );
}

function OtpModal({ order, onClose, onSubmit, error, submitting }) {
  const [otp, setOtp] = useState('');
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 20,
    }}>
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 20, padding: 24, width: '100%', maxWidth: 340 }}>
        <div style={{ fontSize: 17, fontWeight: 800, color: C.text, marginBottom: 6 }}>Confirm Delivery</div>
        <div style={{ fontSize: 13, color: C.textDim, marginBottom: 18 }}>
          Ask the customer for the 4-digit OTP shared with order #{order.orderId} and enter it below.
        </div>
        <input
          value={otp}
          onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 4))}
          placeholder="Enter OTP"
          inputMode="numeric"
          style={{ ...inputStyle, textAlign: 'center', fontSize: 22, letterSpacing: 8, fontWeight: 800, marginBottom: 12 }}
        />
        {error && (
          <div style={{ background: C.danger + '15', color: C.danger, padding: '10px 14px', borderRadius: 10, fontSize: 13, fontWeight: 600, marginBottom: 12 }}>
            {error}
          </div>
        )}
        <div style={{ display: 'flex', gap: 10 }}>
          <Button variant="ghost" onClick={onClose} style={{ flex: 1 }}>Cancel</Button>
          <Button onClick={() => onSubmit(otp)} disabled={otp.length !== 4 || submitting} style={{ flex: 1 }}>
            {submitting ? 'Checking...' : 'Confirm'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function DeliveriesScreen({ token }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [otpOrder, setOtpOrder] = useState(null);
  const [otpError, setOtpError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchOrders = useCallback(async () => {
    try {
      const res = await fetch(`${API}/orders/my`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setOrders(Array.isArray(data) ? data : []);
    } catch (e) {}
    setLoading(false);
  }, [token]);

  useEffect(() => {
    fetchOrders();
    const iv = setInterval(fetchOrders, 8000);
    return () => clearInterval(iv);
  }, [fetchOrders]);

  const updateStatus = async (order, status, otp) => {
    const res = await fetch(`${API}/orders/${order._id}/status`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ status, otp }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Something went wrong');
    fetchOrders();
  };

  const handleAction = (order, status) => {
    if (status === 'delivered') {
      setOtpOrder(order);
      setOtpError('');
    } else {
      updateStatus(order, status).catch(() => {});
    }
  };

  const confirmOtp = async (otp) => {
    setSubmitting(true);
    setOtpError('');
    try {
      await updateStatus(otpOrder, 'delivered', otp);
      setOtpOrder(null);
    } catch (e) {
      setOtpError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const active = orders.filter(o => o.status !== 'delivered');
  const done = orders.filter(o => o.status === 'delivered');

  return (
    <div style={{ padding: '20px 16px 100px' }}>
      <div style={{ fontSize: 20, fontWeight: 800, color: C.text, marginBottom: 20 }}>My Deliveries</div>

      {loading ? (
        <div style={{ textAlign: 'center', color: C.textDim, padding: 40 }}>Loading...</div>
      ) : (
        <>
          {active.length > 0 && (
            <>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.textDim, marginBottom: 10, textTransform: 'uppercase' }}>Active</div>
              {active.map((o) => (
                <OrderCard
                  key={o._id}
                  order={o}
                  actionLabel={o.status === 'out_for_delivery' ? 'Mark Delivered' : 'Picked Up'}
                  onAction={() => handleAction(o, o.status === 'out_for_delivery' ? 'delivered' : 'out_for_delivery')}
                />
              ))}
            </>
          )}
          {done.length > 0 && (
            <>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.textDim, margin: '20px 0 10px', textTransform: 'uppercase' }}>Completed</div>
              {done.map((o) => (
                <OrderCard key={o._id} order={o} />
              ))}
            </>
          )}
          {orders.length === 0 && (
            <EmptyState icon="🚴" title="No deliveries yet" sub="Accepted orders will show up here" />
          )}
        </>
      )}

      {otpOrder && (
        <OtpModal
          order={otpOrder}
          error={otpError}
          submitting={submitting}
          onClose={() => setOtpOrder(null)}
          onSubmit={confirmOtp}
        />
      )}
    </div>
  );
}

// ---------- Earnings Screen ----------
function EarningsBarChart({ history }) {
  const max = Math.max(1, ...history.map((h) => h.earnings));
  const dayLabel = (dateStr) => new Date(dateStr).toLocaleDateString('en-IN', { weekday: 'short' });
  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: 18, marginBottom: 16 }}>
      <div style={{ fontSize: 13, color: C.textDim, fontWeight: 700, marginBottom: 16 }}>LAST 7 DAYS</div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, height: 120 }}>
        {history.map((h, i) => {
          const barH = Math.max(4, (h.earnings / max) * 100);
          return (
            <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
              <div style={{ fontSize: 10, color: C.textFaint }}>{h.earnings > 0 ? fmtMoney(h.earnings) : ''}</div>
              <div style={{
                width: '100%', maxWidth: 28, height: barH, borderRadius: 6,
                background: h.earnings > 0 ? `linear-gradient(180deg, ${C.primary}, ${C.primaryDark})` : C.border,
                transition: 'height .3s',
              }} />
              <div style={{ fontSize: 10, color: C.textFaint, fontWeight: 700 }}>{dayLabel(h.date)}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function EarningsScreen({ token }) {
  const [data, setData] = useState({ totalDeliveries: 0, totalEarnings: 0 });
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      fetch(`${API}/earnings`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
      fetch(`${API}/earnings/history`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
    ]).then(([totals, hist]) => {
      setData(totals);
      setHistory(Array.isArray(hist) ? hist : []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [token]);

  return (
    <div style={{ padding: '20px 16px 100px' }}>
      <div style={{ fontSize: 20, fontWeight: 800, color: C.text, marginBottom: 20 }}>Earnings</div>

      <div style={{
        background: `linear-gradient(135deg, ${C.primary}, ${C.primaryDark})`, borderRadius: 20, padding: 24,
        marginBottom: 20, boxShadow: `0 12px 30px ${C.primary}30`,
      }}>
        <div style={{ color: '#08150c', opacity: 0.7, fontSize: 13, fontWeight: 700 }}>TOTAL EARNINGS</div>
        <div style={{ color: '#08150c', fontSize: 36, fontWeight: 900, marginTop: 4 }}>
          {loading ? '...' : fmtMoney(data.totalEarnings)}
        </div>
      </div>

      {!loading && history.length > 0 && <EarningsBarChart history={history} />}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: 18 }}>
          <div style={{ fontSize: 13, color: C.textDim, fontWeight: 600 }}>Deliveries</div>
          <div style={{ fontSize: 26, fontWeight: 800, color: C.text, marginTop: 4 }}>{loading ? '...' : data.totalDeliveries}</div>
        </div>
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: 18 }}>
          <div style={{ fontSize: 13, color: C.textDim, fontWeight: 600 }}>Per Delivery</div>
          <div style={{ fontSize: 26, fontWeight: 800, color: C.text, marginTop: 4 }}>₹30</div>
        </div>
      </div>

      {!loading && data.cashOrderCount > 0 && (
        <div style={{ background: C.warn + '15', border: `1px solid ${C.warn}40`, borderRadius: 16, padding: 18 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 13, color: C.warn, fontWeight: 700 }}>💵 Cash to Settle</div>
              <div style={{ fontSize: 12, color: C.textDim, marginTop: 2 }}>{data.cashOrderCount} cash order{data.cashOrderCount > 1 ? 's' : ''} collected</div>
            </div>
            <div style={{ fontSize: 22, fontWeight: 800, color: C.warn }}>{fmtMoney(data.cashToSettle)}</div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- Profile Screen ----------
function ProfileScreen({ token, driver, onLogout }) {
  const [full, setFull] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/profile`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((d) => { setFull(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [token]);

  const d = full || driver || {};
  const joinDate = d.createdAt ? new Date(d.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '-';
  const vehicleLabel = { bike: 'Bike', scooter: 'Scooter', cycle: 'Cycle' }[d.vehicleType] || d.vehicleType || '-';

  return (
    <div style={{ padding: '20px 16px 100px' }}>
      <div style={{ fontSize: 20, fontWeight: 800, color: C.text, marginBottom: 20 }}>Profile</div>

      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 18, padding: 22, marginBottom: 16, textAlign: 'center' }}>
        <div style={{
          width: 72, height: 72, borderRadius: 99, background: `linear-gradient(135deg, ${C.primary}, ${C.primaryDark})`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px', fontSize: 28, fontWeight: 800, color: '#08150c',
        }}>
          {d?.name?.[0]?.toUpperCase() || 'D'}
        </div>
        <div style={{ fontSize: 18, fontWeight: 800, color: C.text }}>{d?.name || '-'}</div>
        <div style={{ fontSize: 13, color: C.textDim, marginTop: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
          <Icon.phone s={13} /> {d?.phone || '-'}
        </div>
        <div style={{ marginTop: 10, display: 'flex', justifyContent: 'center', gap: 4, color: C.warn }}>
          <Icon.star /><Icon.star /><Icon.star /><Icon.star /><Icon.star />
          <span style={{ color: C.textDim, fontSize: 12, marginLeft: 4 }}>{loading ? '...' : (d.rating || 5).toFixed(1)}</span>
        </div>
      </div>

      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 18, padding: 4, marginBottom: 16 }}>
        {[
          { label: 'Vehicle Type', value: loading ? '...' : vehicleLabel, icon: Icon.bike },
          { label: 'Vehicle Number', value: loading ? '...' : (d.vehicleNumber || '-'), icon: Icon.bike },
          { label: 'Total Deliveries', value: loading ? '...' : (d.totalDeliveries ?? 0), icon: Icon.orders },
          { label: 'Joined On', value: loading ? '...' : joinDate, icon: Icon.profile },
        ].map((row, i) => (
          <div key={i} style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px',
            borderBottom: i < 3 ? `1px solid ${C.border}` : 'none',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: C.textDim, fontSize: 13 }}>
              <row.icon s={16} />
              {row.label}
            </div>
            <div style={{ color: C.text, fontWeight: 700, fontSize: 14 }}>{row.value}</div>
          </div>
        ))}
      </div>

      <Button variant="danger" full onClick={onLogout}>Logout</Button>
    </div>
  );
}

// ---------- Bottom Nav ----------
function BottomNav({ tab, setTab }) {
  const items = [
    { id: 'home', label: 'Home', icon: Icon.home },
    { id: 'deliveries', label: 'Deliveries', icon: Icon.orders },
    { id: 'earnings', label: 'Earnings', icon: Icon.wallet },
    { id: 'profile', label: 'Profile', icon: Icon.profile },
  ];
  return (
    <div style={{
      position: 'fixed', bottom: 0, left: 0, right: 0, background: C.bg2, borderTop: `1px solid ${C.border}`,
      display: 'flex', padding: '10px 8px calc(10px + env(safe-area-inset-bottom))', zIndex: 50,
    }}>
      {items.map((it) => {
        const active = tab === it.id;
        return (
          <button key={it.id} onClick={() => setTab(it.id)} style={{
            flex: 1, background: 'none', border: 'none', cursor: 'pointer',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
            color: active ? C.primary : C.textFaint, padding: '4px 0',
          }}>
            <it.icon s={22} />
            <span style={{ fontSize: 10, fontWeight: 700 }}>{it.label}</span>
          </button>
        );
      })}
    </div>
  );
}

// ---------- Emergency SOS ----------
function SosButton({ token }) {
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const triggerSos = () => {
    setSending(true);
    const send = (lat, lng) => {
      fetch(`${API}/sos`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ lat, lng, note: 'Emergency SOS triggered from app' }),
      }).finally(() => { setSending(false); setSent(true); });
      if (navigator.vibrate) navigator.vibrate([300, 100, 300, 100, 300]);
    };
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => send(pos.coords.latitude, pos.coords.longitude),
        () => send(null, null),
        { timeout: 8000 }
      );
    } else {
      send(null, null);
    }
  };

  return (
    <>
      <button
        onClick={() => { setOpen(true); setSent(false); }}
        style={{
          position: 'fixed', right: 16, bottom: 92, width: 54, height: 54, borderRadius: 99,
          background: C.danger, border: 'none', color: '#fff', display: 'flex', alignItems: 'center',
          justifyContent: 'center', boxShadow: `0 8px 20px ${C.danger}60`, cursor: 'pointer', zIndex: 40,
          fontSize: 11, fontWeight: 900,
        }}
      >
        SOS
      </button>

      {open && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 20,
        }}>
          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 20, padding: 24, width: '100%', maxWidth: 340, textAlign: 'center' }}>
            {!sent ? (
              <>
                <div style={{ fontSize: 40, marginBottom: 10 }}>🆘</div>
                <div style={{ fontSize: 17, fontWeight: 800, color: C.text, marginBottom: 6 }}>Emergency SOS</div>
                <div style={{ fontSize: 13, color: C.textDim, marginBottom: 20 }}>
                  This will send your live location to SashtiMedix support immediately. Only use this in a genuine emergency.
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <Button variant="ghost" onClick={() => setOpen(false)} style={{ flex: 1 }}>Cancel</Button>
                  <Button variant="danger" onClick={triggerSos} disabled={sending} style={{ flex: 1 }}>
                    {sending ? 'Sending...' : 'Send SOS'}
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div style={{ fontSize: 40, marginBottom: 10 }}>✅</div>
                <div style={{ fontSize: 17, fontWeight: 800, color: C.text, marginBottom: 6 }}>Alert Sent</div>
                <div style={{ fontSize: 13, color: C.textDim, marginBottom: 20 }}>
                  Support has been notified with your location. Stay safe.
                </div>
                <Button onClick={() => setOpen(false)} full>Close</Button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

// ---------- Main App ----------
export default function App() {
  const [token, setToken] = useState(localStorage.getItem('driverToken') || '');
  const [driver, setDriver] = useState(() => {
    try { return JSON.parse(localStorage.getItem('driverInfo') || 'null'); } catch { return null; }
  });
  const [tab, setTab] = useState('home');

  const handleLogin = (tok, drv) => { setToken(tok); setDriver(drv); };
  const handleLogout = () => {
    localStorage.removeItem('driverToken');
    localStorage.removeItem('driverInfo');
    setToken(''); setDriver(null); setTab('home');
  };
  const handleDriverUpdate = (drv) => {
    setDriver(drv);
    localStorage.setItem('driverInfo', JSON.stringify(drv));
  };

  useEffect(() => {
    document.body.style.background = C.bg;
    document.body.style.margin = 0;
    document.body.style.fontFamily = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
  }, []);

  // Auto-logout after 30 minutes of inactivity (security)
  useEffect(() => {
    if (!token) return;
    let timer;
    const INACTIVITY_LIMIT = 30 * 60 * 1000;
    const resetTimer = () => {
      clearTimeout(timer);
      timer = setTimeout(() => { handleLogout(); }, INACTIVITY_LIMIT);
    };
    const events = ['mousedown', 'keydown', 'touchstart', 'scroll'];
    events.forEach((e) => window.addEventListener(e, resetTimer));
    resetTimer();
    return () => {
      clearTimeout(timer);
      events.forEach((e) => window.removeEventListener(e, resetTimer));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (!token) return <LoginScreen onLogin={handleLogin} />;

  return (
    <div style={{ minHeight: '100vh', background: C.bg, color: C.text }}>
      {tab === 'home' && <HomeScreen token={token} driver={driver} onDriverUpdate={handleDriverUpdate} />}
      {tab === 'deliveries' && <DeliveriesScreen token={token} />}
      {tab === 'earnings' && <EarningsScreen token={token} />}
      {tab === 'profile' && <ProfileScreen token={token} driver={driver} onLogout={handleLogout} />}
      <SosButton token={token} />
      <BottomNav tab={tab} setTab={setTab} />
    </div>
  );
}
